import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import AbstractShape from "@/components/AbstractShape";
import { HOMEPAGE_FAQS } from "@/lib/faqData";
import { goToIntake } from "@/lib/intake";
import useSEO from "@/lib/useSEO";
import { faqPageSchema, breadcrumbSchema } from "@/lib/schema";

export default function FAQ() {
  useSEO({
    title: "FAQ | Melbi Wellness",
    description:
      "Common questions about Melbi Wellness — eligibility, clinical review, pricing, pharmacy fulfillment, GLP-1 flat-rate care, and state availability.",
    url: "https://melbiwellness.com/faq",
    jsonLd: [
      faqPageSchema(HOMEPAGE_FAQS),
      breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "FAQ", url: "/faq" },
      ]),
    ],
  });
  return (
    <div data-testid="faq-page">
      <section className="relative">
        <AbstractShape variant="gradient-cream" colors={["#F5E6D3", "#E8D5C4", "#FAF9F6"]} className="absolute inset-0" testId="faq-hero-bg" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 md:pt-24 md:pb-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A92]">FAQ</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-[#2C2B29] max-w-3xl">
            Everything you need to know about Melbi
          </h1>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
          {HOMEPAGE_FAQS.map((f, i) => (
            <AccordionItem
              key={f.q}
              value={`item-${i}`}
              data-testid={`faq-page-item-${i}`}
              className="bg-white rounded-2xl border border-[#E8E5DF] px-5 [&[data-state=open]]:shadow-[0_8px_32px_rgba(0,0,0,0.05)]"
            >
              <AccordionTrigger className="text-left font-display text-base md:text-lg font-semibold text-[#2C2B29] hover:no-underline py-5">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-[#5E5C58] leading-relaxed pb-5 text-sm md:text-base">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 text-center">
        <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#2C2B29]">
          Still have questions?
        </h2>
        <p className="mt-3 text-[#5E5C58]">
          Reach out at <a href="mailto:hello@melbiwellness.com" className="text-[#C96A4E] font-semibold">hello@melbiwellness.com</a>
        </p>
        <button
          data-testid="faq-start-now-cta"
          className="mt-6 inline-flex items-center justify-center bg-[#C96A4E] text-white rounded-full px-8 py-4 text-base font-semibold hover:bg-[#B35A40] transition-colors" type="button" onClick={goToIntake}>
          Start now
          <ArrowRight size={18} className="ml-2" />
        </button>
      </section>
    </div>
  );
}
